import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ChildFriendlyOutlinedIcon from '@mui/icons-material/ChildFriendlyOutlined';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import { format } from 'date-fns';
import Flower from '@/components/icons/Flower';
import InjuryIcon from '@/components/icons/InjuryIcon';
import { BankOfHoursBalanceResponse } from '@/services/ptos';
import { PTOHoursEntry, PTORequest, PTOType, PTOTypeBalance } from '@/types/api';
import { isSameDate } from './dates';

export const getTotalPTORequestHours = (ptoRequest: PTORequest): number =>
  (ptoRequest.entries || []).reduce((hoursSum, { hours }) => hours + hoursSum, 0);

export const getPTORequestLastDay = (ptoRequest: PTORequest): Date =>
  (ptoRequest.entries || []).reduce(
    (resultDate, entry) => (new Date(entry.date) > resultDate ? new Date(entry.date) : resultDate),
    new Date(ptoRequest.firstDay)
  );

export const formatPTORequestDate = (date: Date): string => format(new Date(date), 'MMM d, yyyy');

export const getPTORequestIntervalText = (ptoRequest: PTORequest): string => {
  const lastDay = getPTORequestLastDay(ptoRequest);

  if (isSameDate(new Date(ptoRequest.firstDay), lastDay)) {
    return formatPTORequestDate(new Date(ptoRequest.firstDay));
  }

  return `${formatPTORequestDate(new Date(ptoRequest.firstDay))} - ${formatPTORequestDate(
    lastDay
  )}`;
};

function isChristmas(date: Date): boolean {
  return date.getDate() === 25 && date.getMonth() === 11;
}

function isNewYearsDay(date: Date): boolean {
  return date.getDate() === 1 && date.getMonth() === 0;
}

export function isWeekendOrFixedHoliday(date: Date): boolean {
  return date.getDay() === 0 || date.getDay() === 6 || isNewYearsDay(date) || isChristmas(date);
}

export function isMondayAfterHolidayOnWeekend(date: Date): boolean {
  if (date.getDay() !== 1) return false;

  const sunday = new Date(date);
  sunday.setDate(sunday.getDate() - 1);
  const saturday = new Date(date);
  saturday.setDate(saturday.getDate() - 2);

  if (
    isChristmas(sunday) ||
    isChristmas(saturday) ||
    isNewYearsDay(sunday) ||
    isNewYearsDay(saturday)
  ) {
    return true;
  }
  return false;
}

export function getBusinessDates(startDate: Date, endDate: Date): Date[] {
  const array: Date[] = [];
  const curDate = new Date(startDate.getTime());
  while (curDate <= endDate) {
    if (!isWeekendOrFixedHoliday(curDate) && !isMondayAfterHolidayOnWeekend(curDate))
      array.push(new Date(curDate.getTime()));
    curDate.setDate(curDate.getDate() + 1);
  }
  return array;
}

export const createBalanceByPTOType = (
  balanceArray: Array<PTOTypeBalance>
): Partial<Record<PTOType, PTOTypeBalance>> => {
  const balance: Partial<Record<PTOType, PTOTypeBalance>> = {};

  balanceArray.forEach((balanceItem) => {
    balance[balanceItem.type] = { ...balanceItem };
  });

  return balance;
};

export const sortEntriesAscending = (a: PTOHoursEntry, b: PTOHoursEntry): number =>
  new Date(a.date).getTime() - new Date(b.date).getTime();

export const iconByPTOType: { [type in PTOType]: JSX.Element } = {
  [PTOType.PERSONAL_PAUSE]: <BeachAccessIcon />,
  [PTOType.HEALTH_PAUSE]: <ThermostatIcon />,
  [PTOType.PRIMARY_CAREGIVER]: <ChildFriendlyOutlinedIcon />,
  [PTOType.SECONDARY_CAREGIVER]: <ChildFriendlyOutlinedIcon />,
  [PTOType.GRIEF_PAUSE]: <Flower />,
  [PTOType.HEALTH_LEAVE]: <InjuryIcon />,
};

export const ptoTypeLabel: { [type in PTOType]: string } = {
  PERSONAL_PAUSE: 'Personal Time Off',
  HEALTH_PAUSE: 'Sick Time',
  GRIEF_PAUSE: 'Grief Time',
  PRIMARY_CAREGIVER: 'Primary Caregiver Leave',
  SECONDARY_CAREGIVER: 'Secondary Caregiver Leave',
  HEALTH_LEAVE: 'Medical Leave',
};

export const doestHaveEnoughHours = (
  type: PTOType,
  hours: number,
  balance: BankOfHoursBalanceResponse
): boolean => {
  const balanceItem = balance.find((item) => item.type === type);

  if (!balanceItem || balanceItem.unlimited || balanceItem.availableHours >= hours) return false;

  return true;
};

export const formatPtoTypeHours = (
  type: PTOType,
  hours: number
): { value: number; label: 'days' | 'hours' } => {
  if (type === PTOType.PRIMARY_CAREGIVER || type === PTOType.GRIEF_PAUSE) {
    return {
      value: Math.ceil(hours / 8),
      label: 'days',
    };
  }

  return {
    value: hours,
    label: 'hours',
  };
};
